import { ApiError } from '~/api/errors';

import type { EnrichmentRunConfig } from '../types/enrichment-run-config';
import type { JobRunConfig } from '../types/job-run-config.types';

/**
 * Phase statuses during which the backend refuses run-config edits (409): a live workflow
 * keeps the config it started with. Compared as strings because the polled status can carry
 * values (`DELIVERING`) the `EnrichmentJobStatus` union doesn't model.
 */
const LIVE_PHASE_STATUSES = new Set(['PENDING', 'IN_PROGRESS', 'AWAITING_APPROVAL', 'DELIVERING']);

/** Whether any of the job's phases is live, i.e. run-config editing should be locked. */
export function isAnyPhaseLive(statuses: readonly (string | null | undefined)[]): boolean {
  return statuses.some((s) => s != null && LIVE_PHASE_STATUSES.has(s.toUpperCase()));
}

/**
 * Stored job config → the funnel's local run config, which every later trigger spreads into
 * its body. Fields with no explicit choice are dropped so the trigger inherits the stored
 * value field by field (the contract's "send nothing" path). Scoring `null` means "runs", so
 * it maps to `true` — the server records `null → true` as no change.
 */
export function toEnrichmentRunConfig(config: JobRunConfig): EnrichmentRunConfig {
  const sd = config.searchDescriptors;
  const scoring = config.scoring;
  return {
    ...(config.contentAttributes?.length ? { contentAttributes: config.contentAttributes } : {}),
    // The store shape needs both descriptor flags; a half-null pair is left to the server.
    ...(sd?.enabled != null && sd.phraseDescriptions != null
      ? { searchDescriptors: { enabled: sd.enabled, phraseDescriptions: sd.phraseDescriptions } }
      : {}),
    ...(scoring
      ? {
          scoring: {
            preScoring: scoring.preScoring ?? true,
            postScoring: scoring.postScoring ?? true,
          },
        }
      : {}),
    ...(config.retailer ? { retailer: config.retailer } : {}),
    ...(config.targetLocales?.length ? { targetLocales: config.targetLocales } : {}),
  };
}

/** Classified PATCH failure, so the editor can pick the right copy and placement. */
export interface RunConfigEditError {
  kind: 'in-progress' | 'validation' | 'other';
  /** Human text from the envelope (`details[].message` / `errors[0]`); absent for 409. */
  message?: string;
  /** Field named by a bean-validation `details[]` entry, e.g. `reason`. */
  field?: string;
}

/** Narrow view of the workflow-service error envelope `{ message, errors[], details[] }`. */
interface WorkflowErrorEnvelope {
  errors?: unknown;
  details?: unknown;
}

/**
 * Classifies a run-config PATCH failure from the workflow-service envelope: 409 → a run is in
 * progress; 400 → validation, with the offending field when `details[]` names one.
 */
export function classifyRunConfigEditError(err: unknown): RunConfigEditError {
  if (!(err instanceof ApiError)) return { kind: 'other' };
  if (err.statusCode === 409) return { kind: 'in-progress' };

  const body = (err.body ?? {}) as WorkflowErrorEnvelope;
  const errors = Array.isArray(body.errors)
    ? body.errors.filter((e): e is string => typeof e === 'string')
    : [];
  const detail = Array.isArray(body.details)
    ? (body.details as { field?: unknown; message?: unknown }[]).find(
        (d) => typeof d.field === 'string',
      )
    : undefined;
  const message =
    (typeof detail?.message === 'string' ? detail.message : undefined) ?? errors[0] ?? err.message;

  if (err.statusCode === 400) {
    return {
      kind: 'validation',
      ...(message ? { message } : {}),
      ...(typeof detail?.field === 'string' ? { field: detail.field } : {}),
    };
  }
  return { kind: 'other', ...(message ? { message } : {}) };
}
